// The page of a pipeline (R-U31, decision 040 п.3, as in amoCRM): «Доска» — its stages with the closed records folded away, «Список» —
// its records, «Настройки» — stages, their rules and scenarios. A pipeline is a page of its own, not a filter of the section.

import { useEffect, useMemo, useState } from 'react';
import { canRead, canWrite, recordTitle, textKey, type Id, type SortKey, type ViewColumn } from '@confdeck/core-shared';
import { Board } from '../board/Board.tsx';
import { newRecordData } from '../record/new-record.ts';
import { createRecord, freshRecords } from '../sync/mutations.ts';
import { useAccess, useDebounced, useMeta, useQuery, useSync } from '../sync/react.tsx';
import { BulkEdit } from '../table/BulkEdit.tsx';
import { DataTable, plural } from '../table/DataTable.tsx';
import { navigate, recordHref, wantsNewTab } from '../shell/router.ts';
import { HERE_LIMIT, useScreenSearch, type ScreenHit } from '../shell/screen-search.ts';
import { rememberList } from '../shell/nav-place.ts';
import { Segmented } from '../ui/controls.tsx';
import { Button, Count } from '../ui/primitives.tsx';
import { TopBar, TopTitle } from '../ui/TopBar.tsx';
import { pageView } from './MineScreen.tsx';
import { pipelineField, pipelineHref, pipelineSectionOf, pipelineType, type PipelineTab } from './pipeline-section.ts';
import { PipelineSettings } from './PipelineSettings.tsx';
import { Gantt } from '../gantt/Gantt.tsx';
import shell from '../shell/shell.module.css';
import s from './pipelines.module.css';

export function PipelineScreen({ pipelineId, tab }: { pipelineId: Id; tab: PipelineTab }) {
  const meta = useMeta();
  const client = useSync();
  const access = useAccess();
  const p = meta.term(pipelineId);
  const typeId = pipelineType(meta, pipelineId);
  const type = meta.type(typeId);
  const def = pipelineSectionOf(type?.key);
  const field = typeId ? pipelineField(meta, typeId) : undefined;
  const [searchText, setSearch] = useState('');
  const search = useDebounced(searchText, 80);
  const [selected, setSelected] = useState<Set<Id>>(new Set());
  const [bulk, setBulk] = useState(false);
  const filter = useMemo(() => (field ? { field: field.key, op: 'under' as const, value: pipelineId } : null), [field, pipelineId]);
  // The list's own columns and sort, for this visit (MiniMax review wave A, P3-1); the board orders its cards itself.
  const listFields = type && field ? [type.titleField, field.key, ...(def?.list ?? []).filter((c) => c !== field.key && c !== `${field.key}:pipeline`)] : [];
  const [own, setColumns] = useState<ViewColumn[] | null>(null);
  const columns = own ?? (type ? pageView(type.id, '', '', 'table', listFields, null).columns : []);
  const [sort, setSort] = useState<SortKey[]>([]);
  const q = useQuery(typeId ?? '', filter, tab === 'list' ? sort : [], search);
  // A section with a gantt view (the tasks) shows its category on the same gantt: dates and dependencies of this pipeline only.
  const gantt = typeId ? meta.views(typeId).find((v) => v.layout === 'gantt' && !v.archivedAt) : undefined;
  // The board hides the closed records (R-U31): its count is of the open ones it shows; the list counts all.
  const shownCount = tab === 'board' && field ? q.ids.filter((id) => !meta.term(String(client.store.record(id)?.data[field.key] ?? ''))?.final).length : q.ids.length;
  // The crumb of a record opened here and its «назад» lead back to this page.
  useEffect(() => { if (typeId) rememberList(typeId, pipelineHref(pipelineId, tab === 'settings' ? undefined : tab)); }, [typeId, pipelineId, tab]);

  useScreenSearch(type ? (query) => {
    const k = textKey(query);
    const hits: ScreenHit[] = [];
    for (const id of q.ids) {
      const title = recordTitle(client.store.record(id), meta);
      if (textKey(title).includes(k)) hits.push({ id, recordId: id, label: title, hint: p?.name, icon: type.icon, run: () => navigate(recordHref(id)) });
      if (hits.length >= HERE_LIMIT) break;
    }
    return hits;
  } : null);

  const word = def?.words.pipeline ?? 'Воронка';
  if (!p || p.archivedAt || !type || !field) return <div className={shell.empty}>{word} не найдена или в архиве. Вернуть её можно в «Настройки → Структура».</div>;
  if (!canRead(access, type.id)) return <div className={shell.empty}>Нет доступа к разделу «{type.nameMany}».</div>;
  const open = (id: Id, e?: React.MouseEvent | React.KeyboardEvent) => (wantsNewTab(e as React.MouseEvent) ? window.open(recordHref(id), '_blank') : navigate(recordHref(id)));
  const writable = canWrite(access, type.id);
  const create = () => {
    const id = createRecord(client, type.id, newRecordData(meta, type.id, 'Без названия', { pipelineId }));
    freshRecords.add(id);
    open(id);
  };
  // The board shows the pipeline's own stages: the column of the pipeline would say the same on every card.
  const card = (def?.card ?? []).filter((c) => c !== `${field.key}:pipeline`);

  return (
    <div className={shell.typeScreen}>
      <TopBar heading={`${word} «${p.name}»`}
        start={<TopTitle><span className={s.pageTitle}><span className={s.swatch} style={{ background: `var(--c-${p.color}-dot)` }} />{p.name}</span></TopTitle>}
        middle={<Segmented size="sm" label={`Вкладки: ${word.toLowerCase()}`} value={tab} onChange={(v) => navigate(pipelineHref(pipelineId, v), { replace: true })}
          items={[{ value: 'board', label: 'Доска' }, { value: 'list', label: 'Список' }, ...(gantt ? [{ value: 'gantt' as const, label: 'Гант' }] : []), { value: 'settings', label: 'Настройки' }]} />}
        search={tab === 'settings' ? undefined : { value: searchText, onChange: setSearch, placeholder: `Найти в «${p.name}»`, slash: true }}
        count={tab === 'settings' ? undefined : <Count value={shownCount.toLocaleString('ru-RU')} />}
        actions={writable && tab !== 'settings' ? [{ key: 'create', label: type.name, icon: 'plus', primary: true, onClick: create }] : []} />
      {tab === 'settings' ? <PipelineSettings pipelineId={pipelineId} /> : (
        <div className={shell.typeBody}>
          <div className={shell.listArea}>
            {tab === 'gantt' && gantt ? (
              <Gantt typeId={type.id} view={gantt} ids={q.ids} onOpen={open} openId={null} />
            ) : tab === 'board' ? (
              <Board typeId={type.id} view={pageView(type.id, `pipeline:${pipelineId}`, p.name, 'board', [type.titleField, ...card], { field: field.key, pipelineId, finals: 'fold' })} ids={q.ids} onOpen={open} openId={null} />
            ) : q.ids.length === 0 ? (
              <div className={shell.empty}><div>{search ? 'Ничего не нашлось.' : `В «${p.name}» пока нет записей.`}</div>{writable && <Button variant="primary" icon="plus" onClick={create}>{type.name}</Button>}</div>
            ) : (
              <DataTable typeId={type.id} ids={q.ids} columns={columns} onColumns={setColumns}
                sortState={sort} onSort={(f, dir) => setSort(dir ? [{ field: f, dir }] : [])} onOpen={open} selected={selected} onSelect={setSelected} />
            )}
            {tab === 'list' && selected.size > 0 && (
              <div className={shell.bulk} role="toolbar" aria-label="Действия с выбранными">
                <span>Выбрано {selected.size} {plural(selected.size, 'запись', 'записи', 'записей')}</span>
                {writable && <Button size="sm" icon="edit" onClick={() => setBulk(true)}>Изменить поле</Button>}
                <Button size="sm" icon="close" onClick={() => setSelected(new Set())}>Снять выбор</Button>
              </div>
            )}
            {bulk && <BulkEdit typeId={type.id} ids={[...selected]} onClose={() => { setBulk(false); setSelected(new Set()); }} />}
          </div>
        </div>
      )}
    </div>
  );
}
